import React, { useEffect, useMemo, useState } from 'react';

interface HarvestProgressOverlayProps {
  isOpen: boolean;
  nodeName: string;
  startedAt: number;
  harvestTimeSeconds: number;
  onCancel: () => void;
  onComplete: () => void;
}

export const HarvestProgressOverlay: React.FC<HarvestProgressOverlayProps> = ({
  isOpen,
  nodeName,
  startedAt,
  harvestTimeSeconds,
  onCancel,
  onComplete,
}): React.ReactElement | null => {
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    if (!isOpen) return;

    const interval = window.setInterval(() => {
      setTick((v) => v + 1);
    }, 50);

    return () => window.clearInterval(interval);
  }, [isOpen]);

  const { progress, remainingSeconds, isDone } = useMemo(() => {
    const durationMs = harvestTimeSeconds * 1000;
    const elapsedMs = Date.now() - startedAt + tick * 0;
    const clampedElapsed = Math.max(0, Math.min(durationMs, elapsedMs));
    const p = durationMs > 0 ? clampedElapsed / durationMs : 1;
    const remaining = Math.max(0, (durationMs - clampedElapsed) / 1000);
    return { progress: p, remainingSeconds: remaining, isDone: p >= 1 };
  }, [harvestTimeSeconds, startedAt, tick]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isDone) return;
    onComplete();
  }, [isDone, isOpen, onComplete]);

  if (!isOpen) return null;

  return (
    <div
      className="font-mono"
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        width: 320,
        background: 'rgba(13, 17, 23, 0.95)',
        border: '2px solid #2ecc71',
        padding: 12,
        zIndex: 400,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ color: '#2ecc71', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>HARVESTING</div>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: 'transparent',
            border: '1px solid #30363d',
            color: '#8b949e',
            cursor: 'pointer',
            padding: '4px 8px',
            fontSize: 11,
          }}
        >
          Cancel
        </button>
      </div>

      <div style={{ color: '#c9d1d9', fontSize: 12, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {nodeName}
      </div>

      <div style={{ height: 10, background: '#161b22', border: '1px solid #30363d', marginBottom: 8 }}>
        <div
          style={{
            height: '100%',
            width: `${Math.round(progress * 100)}%`,
            background: '#2ecc71',
            transition: 'width 50ms linear',
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b949e' }}>
        <span>{Math.round(progress * 100)}%</span>
        <span>{remainingSeconds.toFixed(1)}s</span>
      </div>
    </div>
  );
};
